import {
  ReactElement,
  KeyboardEvent,
  MouseEvent,
  useState,
  useCallback,
  WeakValidationMap,
  useImperativeHandle,
  Ref,
  forwardRef,
  HTMLAttributes,
} from 'react';
import PropTypes, { Validator } from 'prop-types';
import { themeContextPropTypes, ThemeContext, useTheme } from './ThemeContext';
import KeyboardUserContext from './KeyboardUserContext';
import ModalManagerContext from './ModalManagerContext';
import ModalManager from '@restart/ui/ModalManager';
import { forgeClassHelper } from '../utils/classes';
import { contextDefaultValues, Theme } from './context';
import PortalProvider from '../PortalProvider';

const classes = forgeClassHelper({ name: 'root', isPortal: true });

export interface RootProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  theme?: Theme;
}

export interface RootComponentProps extends RootProps {
  forwardedRef?: Ref<HTMLDivElement>;
}
function RootDiv({
  theme: upstreamTheme,
  className,
  forwardedRef,
  children,
  ...passthroughProps
}: RootComponentProps): ReactElement {
  const theme = useTheme(upstreamTheme);
  return (
    <div
      {...passthroughProps}
      {...classes({
        theme,
        extra: className,
      })}
      ref={forwardedRef}
    >
      {children}
    </div>
  );
}

function RootComponent({
  theme,
  onKeyDown: passedKeyDown,
  onMouseMove: passedMouseMove,
  forwardedRef,
  children,
  ...passedProps
}: RootComponentProps): ReactElement {
  const [isKeyboardUser, setIsKeyboardUser] = useState(true);
  const [modalManager] = useState(new ModalManager());
  const [rootNode, setRootNode] = useState<HTMLDivElement | null>(null);
  // Forward the ref that root uses internally.
  useImperativeHandle<HTMLDivElement | null, HTMLDivElement | null>(forwardedRef, () => rootNode, [rootNode]);

  const onMouseMove = useCallback(
    (e: MouseEvent<HTMLDivElement>): void => {
      if (isKeyboardUser) {
        setIsKeyboardUser(false);
      }
      if (passedMouseMove) {
        passedMouseMove(e);
      }
    },
    [isKeyboardUser, passedMouseMove]
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>): void => {
      if (e.key === 'Tab' && !isKeyboardUser) {
        setIsKeyboardUser(true);
      }
      if (passedKeyDown) {
        passedKeyDown(e);
      }
    },
    [isKeyboardUser, passedKeyDown]
  );

  return (
    <ThemeContext.Provider value={theme || contextDefaultValues.theme}>
      <ModalManagerContext.Provider value={modalManager}>
        <KeyboardUserContext.Provider value={isKeyboardUser}>
          <PortalProvider rootNode={rootNode}>
            <RootDiv {...passedProps} forwardedRef={setRootNode} onMouseMove={onMouseMove} onKeyDown={onKeyDown}>
              {children}
            </RootDiv>
          </PortalProvider>
        </KeyboardUserContext.Provider>
      </ModalManagerContext.Provider>
    </ThemeContext.Provider>
  );
}

RootComponent.displayName = 'Root';

const rootPropTypes: WeakValidationMap<RootComponentProps & { '...rest': unknown }> = {
  /** Adds a class to the root element of the component */
  className: PropTypes.string,
  /**
   * Theme variables for configuring an instance of Forge. Currently includes:
   *   - rootClasses - Array of classes to add to root DOM elements (in addition to `fe_f_all`)
   */
  theme: themeContextPropTypes,
  /** A ref to the div element that wraps Root */
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    /** Note: { current: PropTypes.instanceOf(HTMLDivElement) } would be more
     *  correct, but causes a StackOverflow when building gatsby.
     */
    PropTypes.shape({ current: PropTypes.any }),
  ]) as Validator<Ref<HTMLDivElement>>,
  /** All other props are passed to the root element. */
  '...rest': PropTypes.any,
};
RootComponent.propTypes = rootPropTypes;

function RootController(props: RootComponentProps, ref: Ref<HTMLDivElement>): ReactElement {
  return <RootComponent {...props} forwardedRef={ref} />;
}

export default forwardRef(RootController);
